import { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard,
  CalendarDays,
  Clock,
  LogOut,
  MapPin,
  Monitor,
  Moon,
  Sun,
  VenetianMask,
} from 'lucide-react';
import './mobile.css';
import MobileDashboard from './MobileDashboard.jsx';
import MobileSchedule from './MobileSchedule.jsx';
import MobileLogtime from './MobileLogtime.jsx';
import MobileStatus from './MobileStatus.jsx';
import MobileMonthHome from './MobileMonthHome.jsx';
import MobileScreenTitle from './MobileScreenTitle.jsx';
import { readViewMode, writeViewMode } from './mobileView.js';
import MyProfileButton from '../components/MyProfileButton.jsx';
import { IS_NATIVE } from '../native/platform.js';

const TABS = [
  { key: 'home', label: 'Accueil', Icon: LayoutDashboard },
  { key: 'schedule', label: 'Planning', Icon: CalendarDays },
  { key: 'logtime', label: 'Saisie des temps', Icon: Clock },
];

/**
 * Sprint App native — contrat d'auth §7 bis : en V1, l'app native ne sert
 * qu'au collaborateur, pour SES données. Pas de tableau de bord d'équipe, pas
 * de profil (photo), pas d'impersonation. La PWA web garde ses 3 vues (TABS).
 * #93 — l'accueil « Mon mois » (remplissage du mois) ouvre l'app.
 */
const NATIVE_TABS = [
  { key: 'home', label: 'Accueil', Icon: LayoutDashboard },
  { key: 'schedule', label: 'Mon planning', Icon: CalendarDays },
  { key: 'status', label: 'Mon statut', Icon: MapPin },
  { key: 'logtime', label: 'Mes imputations', Icon: Clock },
];

/** Écrans proposant la bascule « Liste | Calendrier » (mémorisée par écran). */
const VIEW_MODE_SCREENS = ['schedule', 'logtime'];

/**
 * Shell mobile (PWA). Périmètre : lecture Dashboard + Schedule, lecture +
 * saisie Logtime. Planning et saisie en vue semaine (liste) ou mois (calendrier).
 * Ne touche pas aux vues desktop.
 */
export default function MobileApp({
  user,
  activeView,
  onSelectView,
  onImpersonate,
  isImpersonating,
  onStopImpersonation,
  logout,
  theme,
  cycleTheme,
}) {
  // Ticket #39 — l'écran actif (et donc l'URL) est porté par `App.jsx`, qui
  // possède le routeur unique de l'application. Le shell mobile n'expose que
  // 3 onglets : toute autre vue (lien vers un écran desktop ouvert sur mobile)
  // retombe sur l'accueil, sans réécrire l'URL — l'utilisateur garde son lien.
  // App native : l'onglet est un état local (pas de routage par URL dans la
  // WebView, et les vues natives ne sont pas des écrans desktop).
  const [nativeView, setNativeView] = useState('home');
  const tabs = IS_NATIVE ? NATIVE_TABS : TABS;
  const mobileView = IS_NATIVE
    ? nativeView
    : TABS.some((t) => t.key === activeView)
      ? activeView
      : 'home';
  const setMobileView = IS_NATIVE ? setNativeView : onSelectView;

  // #93 — app native : abonnement aux rappels push une fois connecté, et un tap
  // sur une notification (ou sur le widget, #94) ouvre l'écran qu'elle désigne.
  useEffect(() => {
    if (!IS_NATIVE) return undefined;
    let cancelled = false;
    let off = () => {};
    Promise.all([import('../native/nativePush.js'), import('../native/widgetBridge.js')]).then(
      ([{ initNativePush, NATIVE_OPEN_SCREEN_EVENT }, widget]) => {
        if (cancelled) return;
        const onOpen = (e) => {
          if (NATIVE_TABS.some((t) => t.key === e.detail)) setNativeView(e.detail);
        };
        window.addEventListener(NATIVE_OPEN_SCREEN_EVENT, onOpen);
        // #94 — widget iOS : photo rafraîchie à l'ouverture de la session, et
        // écran demandé par un tap sur le widget au démarrage à froid.
        const screen = widget.startWidgetSession();
        if (screen && NATIVE_TABS.some((t) => t.key === screen)) setNativeView(screen);
        off = () => {
          window.removeEventListener(NATIVE_OPEN_SCREEN_EVENT, onOpen);
          widget.stopWidgetSession();
        };
        void initNativePush();
      },
    );
    return () => {
      cancelled = true;
      off();
    };
  }, []);
  const activeTab = tabs.find((t) => t.key === mobileView) ?? tabs[0];

  // Mode Liste | Calendrier par écran, relu du stockage local au montage.
  const [viewModes, setViewModes] = useState(() =>
    Object.fromEntries(VIEW_MODE_SCREENS.map((k) => [k, readViewMode(k)])),
  );
  const setViewMode = useCallback((screen, mode) => {
    setViewModes((v) => ({ ...v, [screen]: mode }));
    writeViewMode(screen, mode);
  }, []);
  const hasViewMode = VIEW_MODE_SCREENS.includes(mobileView);

  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  return (
    <div className="m-app">
      {isImpersonating ? (
        <div className="m-imperso">
          <VenetianMask size={15} />
          <span>
            Connecté en tant que <strong>{user.name || user.email}</strong>
          </span>
          <button type="button" onClick={onStopImpersonation}>
            <LogOut size={12} /> Revenir
          </button>
        </div>
      ) : null}

      <header className="m-header">
        <img className="m-header__logo" src="/synolog-logo-horizontal.svg" alt="Synolog" />
        <div className="m-header__spacer" />
        <button
          type="button"
          className="m-iconbtn"
          onClick={cycleTheme}
          aria-label="Changer de thème"
          title="Changer de thème"
        >
          <ThemeIcon size={18} />
        </button>
        {IS_NATIVE ? null : <MyProfileButton className="m-iconbtn" iconSize={18} />}
        <button
          type="button"
          className="m-iconbtn"
          onClick={logout}
          aria-label="Se déconnecter"
          title="Se déconnecter"
        >
          <LogOut size={18} />
        </button>
      </header>

      <MobileScreenTitle
        Icon={activeTab.Icon}
        label={activeTab.label}
        viewMode={hasViewMode ? viewModes[mobileView] : undefined}
        onViewModeChange={hasViewMode ? (mode) => setViewMode(mobileView, mode) : undefined}
      />

      <main className="m-content">
        {!IS_NATIVE && mobileView === 'home' && (
          <MobileDashboard user={user} onImpersonate={onImpersonate} />
        )}
        {IS_NATIVE && mobileView === 'home' && (
          <MobileMonthHome
            onOpen={(screen) => {
              // L'indicateur porte sur le mois : on ouvre la vue calendrier.
              if (VIEW_MODE_SCREENS.includes(screen)) setViewMode(screen, 'calendar');
              setMobileView(screen);
            }}
          />
        )}
        {mobileView === 'schedule' && (
          <MobileSchedule
            user={user}
            selfService={IS_NATIVE}
            viewMode={viewModes.schedule}
          />
        )}
        {IS_NATIVE && mobileView === 'status' && <MobileStatus user={user} />}
        {mobileView === 'logtime' && (
          <MobileLogtime user={user} viewMode={viewModes.logtime} />
        )}
      </main>

      <nav className="m-tabbar" aria-label="Navigation principale">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            className="m-tab"
            data-active={mobileView === key}
            aria-current={mobileView === key ? 'page' : undefined}
            onClick={() => setMobileView(key)}
          >
            <span className="m-tab__icon">
              <Icon size={22} />
            </span>
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
